/**
 * Approved concept 04 motion, adapted only for a scoped React lifecycle.
 * @param {HTMLElement} container
 * @returns {() => void}
 */
/* V2 local-only motion: one requestAnimationFrame timeline owns the looping diagram. */
export function initializeApprovedMotion(container) {
  const root = container.querySelector('[data-motion-v2-root]');
  if (!root) return () => undefined;

  const figure = root.closest('.motion-v2-figure');
  const figureScope = figure || root;
  const canvas = root.querySelector('.flow-v2-canvas');
  const svg = root.querySelector('.flow-v2-lines');
  const pulse = root.querySelector('.flow-v2-pulse');
  const toggle = figureScope.querySelector('[data-motion-v2-toggle]');
  const toggleLabel = figureScope.querySelector('[data-motion-v2-toggle-label]');
  const pauseIcon = figureScope.querySelector('[data-motion-v2-pause]');
  const playIcon = figureScope.querySelector('[data-motion-v2-play]');
  const reducedQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const nodes = Object.fromEntries(
    [...root.querySelectorAll('[data-flow-node]')].map(node => [node.dataset.flowNode, node])
  );
  const paths = Object.fromEntries(
    [...root.querySelectorAll('[data-flow-edge]')].map(path => [path.dataset.flowEdge, path])
  );

  const phases = [
    { start: 0, end: 700, edge: 'input-a-hub', node: 'input-a' },
    { start: 700, end: 1400, edge: 'input-b-hub', node: 'input-b' },
    { start: 1400, end: 2100, edge: 'input-c-hub', node: 'input-c' },
    { start: 2100, end: 3200, node: 'hub' },
    { start: 3200, end: 4400, edge: 'hub-output', node: 'output', output: true },
    { start: 4400, end: Infinity, complete: true }
  ];
  const cycleDuration = 5800;
  let elapsed = 0;
  let frameId;
  let lastTimestamp;
  let playing = false;
  let userPaused = false;
  let documentPaused = document.hidden;
  let viewportPaused = false;
  let reducedMotion = reducedQuery.matches;
  let geometryFrame;
  let geometryReady = false;
  let disposed = false;

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const ease = value => value * value * (3 - 2 * value);
  const autoPaused = () => documentPaused || viewportPaused || reducedMotion;
  const canPlay = () => geometryReady && !userPaused && !autoPaused();

  const updateControl = () => {
    const label = reducedMotion ? '재생할 수 없음' : playing ? '일시정지' : '재생';
    const title = reducedMotion
      ? '축소 모션 설정으로 재생할 수 없음'
      : label === '일시정지' ? '애니메이션 일시정지' : '애니메이션 재생';
    toggle?.setAttribute('aria-label', title);
    toggle?.setAttribute('title', title);
    toggle?.setAttribute('aria-pressed', String(playing));
    toggle?.toggleAttribute('disabled', reducedMotion);
    toggle?.setAttribute('aria-disabled', String(reducedMotion));
    if (reducedMotion) toggle?.setAttribute('aria-describedby', 'motion-v2-reduced-note');
    else toggle?.removeAttribute('aria-describedby');
    pauseIcon?.toggleAttribute('hidden', !playing);
    playIcon?.toggleAttribute('hidden', playing);
    if (toggleLabel) toggleLabel.textContent = label;
  };

  const rectPoint = (rect, svgRect, side, ratio = .5, gap = 8) => {
    if (side === 'right') return { x: rect.right - svgRect.left + gap, y: rect.top - svgRect.top + rect.height * ratio };
    if (side === 'left') return { x: rect.left - svgRect.left - gap, y: rect.top - svgRect.top + rect.height * ratio };
    if (side === 'bottom') return { x: rect.left - svgRect.left + rect.width * ratio, y: rect.bottom - svgRect.top + gap };
    return { x: rect.left - svgRect.left + rect.width * ratio, y: rect.top - svgRect.top - gap };
  };

  const circlePoint = (rect, svgRect, side, ratio = .5) => {
    const radius = Math.min(rect.width, rect.height) / 2;
    const cx = rect.left - svgRect.left + rect.width / 2;
    const cy = rect.top - svgRect.top + rect.height / 2;
    const offset = (ratio - .5) * radius * 1.55;
    const edge = Math.sqrt(Math.max(0, radius * radius - offset * offset));
    if (side === 'left') return { x: cx - edge, y: cy + offset };
    if (side === 'right') return { x: cx + edge, y: cy + offset };
    if (side === 'bottom') return { x: cx + offset, y: cy + edge };
    return { x: cx + offset, y: cy - edge };
  };

  const cubicPath = (from, to, vertical) => {
    if (vertical) {
      const bend = from.y + (to.y - from.y) * .52;
      return 'M ' + from.x.toFixed(2) + ' ' + from.y.toFixed(2) +
        ' C ' + from.x.toFixed(2) + ' ' + bend.toFixed(2) +
        ', ' + to.x.toFixed(2) + ' ' + bend.toFixed(2) +
        ', ' + to.x.toFixed(2) + ' ' + to.y.toFixed(2);
    }
    const bend = from.x + (to.x - from.x) * .52;
    return 'M ' + from.x.toFixed(2) + ' ' + from.y.toFixed(2) +
      ' C ' + bend.toFixed(2) + ' ' + from.y.toFixed(2) +
      ', ' + bend.toFixed(2) + ' ' + to.y.toFixed(2) +
      ', ' + to.x.toFixed(2) + ' ' + to.y.toFixed(2);
  };

  const connect = (fromName, toName, pathName, fromRatio, toRatio) => {
    const from = nodes[fromName]?.getBoundingClientRect();
    const to = nodes[toName]?.getBoundingClientRect();
    const svgRect = svg?.getBoundingClientRect();
    if (!from || !to || !svgRect || !svgRect.width || !svgRect.height) return;
    const vertical = to.top > from.bottom;
    const start = fromName === 'hub'
      ? circlePoint(from, svgRect, vertical ? 'bottom' : 'right', fromRatio)
      : rectPoint(from, svgRect, vertical ? 'bottom' : 'right', fromRatio, 3);
    const end = toName === 'hub'
      ? circlePoint(to, svgRect, vertical ? 'top' : 'left', toRatio)
      : rectPoint(to, svgRect, vertical ? 'top' : 'left', toRatio, 3);
    const path = paths[pathName];
    if (path) path.setAttribute('d', cubicPath(start, end, vertical));
  };

  const positionEdges = () => {
    if (!svg || !canvas) return;
    geometryReady = false;
    const canvasRect = canvas.getBoundingClientRect();
    const shouldStack = canvasRect.width < 560;
    const wasNarrow = figure?.classList.contains('is-narrow');
    if (figure && wasNarrow !== shouldStack) {
      figure.classList.toggle('is-narrow', shouldStack);
      schedulePositionEdges();
      return;
    }
    const width = Math.max(1, canvasRect.width);
    const height = Math.max(1, canvasRect.height);
    svg.setAttribute('viewBox', '0 0 ' + width + ' ' + height);
    svg.setAttribute('width', String(width));
    svg.setAttribute('height', String(height));
    connect('input-a', 'hub', 'input-a-hub', .5, .27);
    connect('input-b', 'hub', 'input-b-hub', .5, .5);
    connect('input-c', 'hub', 'input-c-hub', .5, .73);
    connect('hub', 'output', 'hub-output', .5, .5);
    geometryReady = Object.values(paths).every(path => {
      if (!path.getAttribute('d')) return false;
      const length = path.getTotalLength();
      return Number.isFinite(length) && length > 0;
    });
    if (!geometryReady) return;
    render(reducedMotion ? cycleDuration : elapsed);
    if (!playing && canPlay()) start();
  };

  const schedulePositionEdges = () => {
    if (disposed || geometryFrame) return;
    geometryFrame = window.requestAnimationFrame(() => {
      geometryFrame = undefined;
      positionEdges();
    });
  };

  const resetClasses = () => {
    root.querySelectorAll('.flow-v2-card').forEach(node => node.classList.remove('is-active', 'is-done'));
    nodes.hub?.classList.remove('is-active', 'is-complete');
    nodes.output?.classList.remove('is-arrived');
    Object.values(paths).forEach(path => path.classList.remove('is-active'));
    pulse?.classList.remove('is-output');
    if (pulse) pulse.style.opacity = '0';
  };

  const render = time => {
    resetClasses();
    const current = phases.find(phase => time >= phase.start && time < phase.end) || phases[phases.length - 1];
    phases.forEach(phase => {
      if (phase.node?.startsWith('input') && phase.end <= time) nodes[phase.node]?.classList.add('is-done');
    });
    if (current.node === 'hub' || current.output || current.complete) nodes.hub?.classList.add(current.complete ? 'is-complete' : 'is-active');
    if (current.complete) nodes.output?.classList.add('is-arrived');
    if (current.node?.startsWith('input')) nodes[current.node]?.classList.add('is-active');
    if (current.edge) {
      const path = paths[current.edge];
      if (path) path.classList.add('is-active');
      const progress = clamp((time - current.start) / (current.end - current.start), 0, 1);
      if (path && pulse) {
        const length = path.getTotalLength();
        if (Number.isFinite(length) && length > 0) {
          const point = path.getPointAtLength(length * ease(progress));
          pulse.setAttribute('cx', point.x.toFixed(2));
          pulse.setAttribute('cy', point.y.toFixed(2));
          pulse.style.opacity = '1';
          pulse.classList.toggle('is-output', Boolean(current.output));
        }
      }
    }
    updateControl();
  };

  const stopFrame = () => {
    if (frameId) {
      window.cancelAnimationFrame(frameId);
      frameId = undefined;
    }
  };

  const tick = timestamp => {
    if (disposed || !playing) return;
    if (!geometryReady) {
      frameId = window.requestAnimationFrame(tick);
      return;
    }
    if (lastTimestamp === undefined) lastTimestamp = timestamp;
    elapsed += timestamp - lastTimestamp;
    lastTimestamp = timestamp;
    if (elapsed >= cycleDuration) elapsed %= cycleDuration;
    render(elapsed);
    frameId = window.requestAnimationFrame(tick);
  };

  const start = () => {
    if (disposed || !canPlay()) {
      updateControl();
      return;
    }
    playing = true;
    lastTimestamp = undefined;
    stopFrame();
    frameId = window.requestAnimationFrame(tick);
    updateControl();
  };

  const pause = () => {
    playing = false;
    stopFrame();
    updateControl();
  };

  const onToggle = () => {
    if (reducedMotion) return;
    if (playing) {
      userPaused = true;
      pause();
    } else {
      userPaused = false;
      start();
    }
  };

  const onReducedChange = event => {
    reducedMotion = event.matches;
    if (reducedMotion) {
      pause();
      // Keep the user's current point so disabling reduced motion can resume there.
      render(cycleDuration);
    } else {
      render(elapsed);
      if (canPlay()) start();
    }
  };

  const onVisibilityChange = () => {
    documentPaused = document.hidden;
    if (documentPaused) pause();
    else if (canPlay()) start();
    render(reducedMotion ? cycleDuration : elapsed);
  };

  let observer;
  if ('IntersectionObserver' in window) {
    observer = new IntersectionObserver(entries => {
      viewportPaused = !entries[0].isIntersecting;
      if (viewportPaused) pause();
      else if (canPlay()) start();
      render(reducedMotion ? cycleDuration : elapsed);
    }, { threshold: 0.1 });
    observer.observe(root);
  }

  let resizeObserver;
  if ('ResizeObserver' in window) {
    resizeObserver = new ResizeObserver(schedulePositionEdges);
    resizeObserver.observe(canvas);
  }
  toggle?.addEventListener('click', onToggle);
  reducedQuery.addEventListener?.('change', onReducedChange);
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('resize', schedulePositionEdges, { passive: true });

  schedulePositionEdges();
  if (reducedMotion) render(cycleDuration);
  else start();

  return () => {
    disposed = true;
    stopFrame();
    if (geometryFrame) window.cancelAnimationFrame(geometryFrame);
    observer?.disconnect();
    resizeObserver?.disconnect();
    toggle?.removeEventListener('click', onToggle);
    reducedQuery.removeEventListener?.('change', onReducedChange);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('resize', schedulePositionEdges);
  };
}
